import { useEffect, useState } from "react";
import {
    Table,
    TableBody,
    TableCell,
    TableHeader,
    TableRow,
} from "../ui/table";
import Badge from "../ui/badge/Badge";
import { Pencil, Trash2, Copy, Eye, AlertCircle } from 'lucide-react';
import MoreButton from '../common/MoreButton';
import api from "../../services/api";
import Pagination from "../ui/pagination/Pagination";
import ComponentCard from "../common/ComponentCard";
import Modal from '../common/Modal';
import Alert from '../ui/alert/Alert';
import Label from "../form/Label";
import Input from "../form/input/InputField";
import { useNavigate } from "react-router";

interface TermAssignment {
    id: string;
    productId: string | null;
    offerId: string | null;
    product: { id: string; name: string } | null;
    offer: { id: string; name: string } | null;
}

interface CompanyTerm {
    id: string;
    title: string;
    content?: string;
    version: number;
    active: boolean;
    createdAt: string;
    updatedAt: string;
    companyTermsAssignment: TermAssignment[];
}

interface CompanyTermsTableProps {
    searchTerm?: string;
}

export default function CompanyTermsTable({ searchTerm = "" }: CompanyTermsTableProps) {
    const navigate = useNavigate();
    const [terms, setTerms] = useState<CompanyTerm[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [alertInfo, setAlertInfo] = useState<{
        show: boolean;
        type: "success" | "error" | "warning" | "info";
        message: string;
        title: string;
    } | null>(null);

    // Paginacao
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(10);

    // Modal visualizar
    const [viewModalOpen, setViewModalOpen] = useState(false);
    const [viewingTerm, setViewingTerm] = useState<CompanyTerm | null>(null);
    const [viewLoading, setViewLoading] = useState(false);

    // Modal excluir
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [termToDelete, setTermToDelete] = useState<string | null>(null);

    // Modal duplicar
    const [duplicateModalOpen, setDuplicateModalOpen] = useState(false);
    const [duplicateLoading, setDuplicateLoading] = useState(false);
    const [termToDuplicate, setTermToDuplicate] = useState<string | null>(null);
    const [duplicateTitle, setDuplicateTitle] = useState("");

    // Buscar termos
    const fetchTerms = async () => {
        try {
            setLoading(true);
            setError(null);

            const response = await api.get(`/api/company-terms`);

            if (response.data && response.data.success) {
                setTerms(response.data.terms || []);
            } else {
                setTerms([]);
            }
        } catch (err: unknown) {
            console.error("Erro ao buscar termos:", err);
            setError(err instanceof Error ? err.message : "Erro ao carregar termos de uso");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchTerms();
    }, []);

    // Paginacao
    const handlePageChange = (pageNumber: number) => {
        setCurrentPage(pageNumber);
    };

    // Resetar pagina ao pesquisar
    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm]);

    // Filtrar por pesquisa
    const filteredTerms = searchTerm
        ? terms.filter(t =>
            t.title.toLowerCase().includes(searchTerm.toLowerCase())
        )
        : terms;

    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = filteredTerms.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(filteredTerms.length / itemsPerPage);

    // Visualizar termo
    const handleView = async (termId: string) => {
        try {
            setViewLoading(true);
            setViewModalOpen(true);

            const response = await api.get(`/api/company-terms/${termId}`);

            if (response.data && response.data.success) {
                setViewingTerm(response.data.terms);
            }
        } catch (err: unknown) {
            console.error("Erro ao buscar termo:", err);
            setAlertInfo({
                show: true,
                type: "error",
                title: "Erro!",
                message: "Erro ao carregar termo."
            });
            setViewModalOpen(false);
        } finally {
            setViewLoading(false);
        }
    };

    // Excluir termo
    const openDeleteModal = (termId: string) => {
        setTermToDelete(termId);
        setDeleteModalOpen(true);
    };

    const handleDelete = async () => {
        if (!termToDelete) return;

        try {
            setDeleteLoading(true);

            const response = await api.delete(`/api/company-terms/${termToDelete}`);

            if (response.data && response.data.success) {
                setAlertInfo({
                    show: true,
                    type: "success",
                    title: "Sucesso!",
                    message: "Termos desativados com sucesso."
                });
                fetchTerms();
            }
        } catch (err: unknown) {
            console.error("Erro ao excluir termos:", err);
            const axiosErr = err as { response?: { data?: { message?: string } } };
            setAlertInfo({
                show: true,
                type: "error",
                title: "Erro!",
                message: axiosErr.response?.data?.message || "Erro ao desativar termos."
            });
        } finally {
            setDeleteLoading(false);
            setDeleteModalOpen(false);
            setTermToDelete(null);
            setTimeout(() => setAlertInfo(null), 3000);
        }
    };

    // Duplicar termo
    const openDuplicateModal = (termId: string) => {
        setTermToDuplicate(termId);
        setDuplicateTitle("");
        setDuplicateModalOpen(true);
    };

    const handleDuplicate = async () => {
        if (!termToDuplicate) return;

        try {
            setDuplicateLoading(true);

            const body: Record<string, string> = {};
            if (duplicateTitle.trim()) {
                body.title = duplicateTitle;
            }

            const response = await api.post(`/api/company-terms/${termToDuplicate}/duplicate`, body);

            if (response.data && response.data.success) {
                setAlertInfo({
                    show: true,
                    type: "success",
                    title: "Sucesso!",
                    message: "Termos duplicados com sucesso."
                });
                fetchTerms();
            }
        } catch (err: unknown) {
            console.error("Erro ao duplicar termos:", err);
            const axiosErr = err as { response?: { data?: { message?: string } } };
            setAlertInfo({
                show: true,
                type: "error",
                title: "Erro!",
                message: axiosErr.response?.data?.message || "Erro ao duplicar termos."
            });
        } finally {
            setDuplicateLoading(false);
            setDuplicateModalOpen(false);
            setTermToDuplicate(null);
            setDuplicateTitle("");
            setTimeout(() => setAlertInfo(null), 3000);
        }
    };

    // Formatar assignments para exibicao
    const formatAssignments = (assignments: TermAssignment[]) => {
        if (!assignments || assignments.length === 0) return "Sem associacoes";

        const hasGlobal = assignments.some(a => !a.productId);
        if (hasGlobal) return "Todos os produtos";

        return assignments.map(a => {
            const productName = a.product?.name || "Produto";
            if (a.offer) {
                return `${productName} (${a.offer.name})`;
            }
            return productName;
        }).join(", ");
    };

    return (
        <>
            {/* Alert */}
            {alertInfo && alertInfo.show && (
                <div className="mb-4">
                    <Alert
                        variant={alertInfo.type}
                        title={alertInfo.title}
                        message={alertInfo.message}
                    />
                </div>
            )}

            <ComponentCard title="Termos de Uso">
                {loading ? (
                    <div className="flex justify-center items-center py-10">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-500"></div>
                    </div>
                ) : error ? (
                    <div className="text-center py-10">
                        <p className="text-red-500">{error}</p>
                    </div>
                ) : filteredTerms.length === 0 ? (
                    <div className="text-center py-10">
                        <p className="text-gray-500 dark:text-gray-400">
                            {searchTerm ? "Nenhum termo encontrado para esta pesquisa." : "Nenhum termo de uso cadastrado."}
                        </p>
                    </div>
                ) : (
                    <div className="overflow-hidden rounded-xl">
                        <div className="max-w-full overflow-x-auto">
                            <Table>
                                <TableHeader className="border-b border-gray-100 dark:border-white/[0.05]">
                                    <TableRow>
                                        <TableCell
                                            isHeader
                                            className="px-5 py-3 font-medium text-gray-500 text-start text-theme-xs dark:text-gray-400"
                                        >
                                            Titulo
                                        </TableCell>
                                        <TableCell
                                            isHeader
                                            className="px-5 py-3 font-medium text-gray-500 text-start text-theme-xs dark:text-gray-400"
                                        >
                                            Versao
                                        </TableCell>
                                        <TableCell
                                            isHeader
                                            className="px-5 py-3 font-medium text-gray-500 text-start text-theme-xs dark:text-gray-400"
                                        >
                                            Associacoes
                                        </TableCell>
                                        <TableCell
                                            isHeader
                                            className="px-5 py-3 font-medium text-gray-500 text-start text-theme-xs dark:text-gray-400"
                                        >
                                            Status
                                        </TableCell>
                                        <TableCell
                                            isHeader
                                            className="px-5 py-3 font-medium text-gray-500 text-start text-theme-xs dark:text-gray-400"
                                        >
                                            Criado em
                                        </TableCell>
                                        <TableCell
                                            isHeader
                                            className="px-5 py-3 font-medium text-gray-500 text-end text-theme-xs dark:text-gray-400 pr-8"
                                        >
                                            Acoes
                                        </TableCell>
                                    </TableRow>
                                </TableHeader>
                                <TableBody className="divide-y divide-gray-100 dark:divide-white/[0.05]">
                                    {currentItems.map((term) => (
                                        <TableRow key={term.id}>
                                            <TableCell className="px-5 py-4 text-start">
                                                <span className="block font-medium text-gray-800 text-theme-sm dark:text-white/90">
                                                    {term.title}
                                                </span>
                                            </TableCell>
                                            <TableCell className="px-4 py-3 text-gray-500 text-start text-theme-sm dark:text-gray-400">
                                                v{term.version}
                                            </TableCell>
                                            <TableCell className="px-4 py-3 text-gray-500 text-start text-theme-sm dark:text-gray-400">
                                                <span className="block text-gray-500 text-theme-xs dark:text-gray-400" title={formatAssignments(term.companyTermsAssignment)}>
                                                    {formatAssignments(term.companyTermsAssignment).length > 40
                                                        ? formatAssignments(term.companyTermsAssignment).substring(0, 40) + "..."
                                                        : formatAssignments(term.companyTermsAssignment)}
                                                </span>
                                            </TableCell>
                                            <TableCell className="px-4 py-3 text-gray-500 text-start text-theme-sm dark:text-gray-400">
                                                <Badge
                                                    size="sm"
                                                    color={term.active ? "success" : "error"}
                                                >
                                                    {term.active ? "Ativo" : "Inativo"}
                                                </Badge>
                                            </TableCell>
                                            <TableCell className="px-5 py-4 text-start">
                                                <span className="block text-gray-500 text-theme-xs dark:text-gray-400">
                                                    {new Date(term.createdAt).toLocaleDateString()}
                                                </span>
                                            </TableCell>
                                            <TableCell className="px-4 py-3 text-gray-500 text-end text-theme-sm dark:text-gray-400 pr-8">
                                                <div className="flex justify-end gap-2 relative">
                                                    <MoreButton
                                                        options={[
                                                            {
                                                                label: "Visualizar",
                                                                onClick: () => handleView(term.id),
                                                                icon: <Eye className="size-4" />
                                                            },
                                                            {
                                                                label: "Editar",
                                                                onClick: () => navigate(`/products/terms/edit/${term.id}`),
                                                                icon: <Pencil className="size-4" />
                                                            },
                                                            {
                                                                label: "Duplicar",
                                                                onClick: () => openDuplicateModal(term.id),
                                                                icon: <Copy className="size-4" />
                                                            },
                                                            {
                                                                label: "Desativar",
                                                                onClick: () => openDeleteModal(term.id),
                                                                icon: <Trash2 className="size-4" />,
                                                                className: "text-red-600 dark:text-red-400"
                                                            }
                                                        ]}
                                                    />
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                            {totalPages > 1 && (
                                <div className="p-4 border-t border-gray-100 dark:border-white/[0.05]">
                                    <Pagination
                                        currentPage={currentPage}
                                        totalPages={totalPages}
                                        onPageChange={handlePageChange}
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </ComponentCard>

            {/* Modal Visualizar */}
            <Modal
                isOpen={viewModalOpen}
                onClose={() => {
                    setViewModalOpen(false);
                    setViewingTerm(null);
                }}
                title={viewingTerm?.title || "Termos de Uso"}
                size="lg"
            >
                {viewLoading ? (
                    <div className="flex justify-center items-center py-10">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-500"></div>
                    </div>
                ) : viewingTerm ? (
                    <div className="space-y-4">
                        <div className="flex gap-4 text-sm text-gray-500 dark:text-gray-400">
                            <span>Versao: <strong>v{viewingTerm.version}</strong></span>
                            <span>Status: <strong>{viewingTerm.active ? "Ativo" : "Inativo"}</strong></span>
                            <span>Criado em: <strong>{new Date(viewingTerm.createdAt).toLocaleDateString()}</strong></span>
                        </div>

                        {viewingTerm.companyTermsAssignment && viewingTerm.companyTermsAssignment.length > 0 && (
                            <div>
                                <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Associacoes:</p>
                                <div className="flex flex-wrap gap-2">
                                    {viewingTerm.companyTermsAssignment.map((a) => (
                                        <Badge key={a.id} size="sm" color="info">
                                            {a.product ? a.product.name : "Global"}
                                            {a.offer ? ` (${a.offer.name})` : ""}
                                        </Badge>
                                    ))}
                                </div>
                            </div>
                        )}

                        <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                            <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Conteudo:</p>
                            <div
                                className="prose prose-sm dark:prose-invert max-w-none max-h-96 overflow-y-auto rounded-lg border border-gray-200 dark:border-gray-700 p-4 bg-gray-50 dark:bg-gray-900"
                                dangerouslySetInnerHTML={{ __html: viewingTerm.content || "" }}
                            />
                        </div>
                    </div>
                ) : null}
            </Modal>

            {/* Modal Excluir */}
            <Modal
                isOpen={deleteModalOpen}
                onClose={() => {
                    setDeleteModalOpen(false);
                    setTermToDelete(null);
                }}
                size="sm"
                onConfirm={handleDelete}
                confirmText="Desativar"
                cancelText="Cancelar"
                confirmButtonClass="bg-red-500 hover:bg-red-600 text-white"
                confirmLoading={deleteLoading}
            >
                <div className="flex flex-col items-center text-center py-4">
                    <AlertCircle className="h-16 w-16 text-red-500 mb-4" />
                    <h3 className="text-lg font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Desativar termos de uso?
                    </h3>
                    <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                        O termo nao aparecera mais no checkout, mas os registros de aceite anteriores serao preservados.
                    </p>
                </div>
            </Modal>

            {/* Modal Duplicar */}
            <Modal
                isOpen={duplicateModalOpen}
                onClose={() => {
                    setDuplicateModalOpen(false);
                    setTermToDuplicate(null);
                    setDuplicateTitle("");
                }}
                title="Duplicar Termos de Uso"
                size="sm"
                onConfirm={handleDuplicate}
                confirmText="Duplicar"
                cancelText="Cancelar"
                confirmLoading={duplicateLoading}
            >
                <div className="space-y-4">
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                        Uma copia do termo sera criada com todas as associacoes.
                    </p>
                    <div>
                        <Label htmlFor="duplicate-title">Novo titulo (opcional)</Label>
                        <Input
                            id="duplicate-title"
                            placeholder="Deixe vazio para usar o titulo original + (copia)"
                            value={duplicateTitle}
                            onChange={(e) => setDuplicateTitle(e.target.value)}
                        />
                    </div>
                </div>
            </Modal>
        </>
    );
}
